/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 29, 2026 by hoarfroster
 *
 */

import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router';
import GoodPinCode from '@components/form/pincode';
import GoodInput from '@components/form/input';
import { Button } from '@components/ui/button';
import { BannerContext } from '@/contexts/banner-context';
import I18N from '@utils/i18n.base';
import { User } from '@models/user/user';
import { hashPassword } from '@utils/crypto';
import { authenticatePasskey } from '@models/user/passkey.ts';
import { beginMicrosoftLogin } from '@models/user/microsoft.ts';
import { setCookie } from '@utils/cookie.ts';

type SignInResult = Awaited<ReturnType<typeof User.signIn>>;
type SignInResponse = Extract<SignInResult, { type: 'success' }>;

export interface SignInFormProps {
    onAuthenticated?: (response?: SignInResponse) => void | Promise<void>;
    showGreeting?: boolean;
    showRegisterLink?: boolean;
    showThirdParty?: boolean;
    showAgreement?: boolean;
    inlineError?: boolean;
    title?: string;
    description?: string;
    className?: string;
}

export default function SignInForm({
    onAuthenticated,
    showGreeting = true,
    showRegisterLink = true,
    showThirdParty = true,
    showAgreement = true,
    inlineError = false,
    title = I18N.auth.signInTitle,
    description = I18N.auth.signInDescription,
    className = '',
}: SignInFormProps) {
    const [submittable, setSubmittable] = useState(true);
    const [userName, setUserName] = useState<string>();
    const [authError, setAuthError] = useState('');
    const [totpChallenge, setTotpChallenge] = useState<string | null>(null);
    const [challengeMessage, setChallengeMessage] = useState('');
    const { setMessage } = useContext(BannerContext);

    const randomGreeting = useMemo(() => {
        const greetings = ['HI THERE', 'BIEN VENUE', '你好', '欢迎返来', 'HOLA', 'こんにちは', 'CIAO'];
        return greetings[Math.floor(Math.random() * greetings.length)];
    }, []);

    const completeAuthentication = async (response?: SignInResponse) => {
        if (onAuthenticated) {
            await onAuthenticated(response);
            return;
        }
        setMessage({ message: I18N.auth.signInSuccessful, type: 'success' });
        setTimeout(() => (window.location.href = '/'), 3000);
    };

    const reportAuthError = (fallback: string, error: unknown) => {
        const nextMessage = error instanceof Error && error.message ? error.message : fallback;
        if (inlineError) {
            setAuthError(nextMessage);
        } else {
            setMessage({ message: nextMessage, type: 'error' });
        }
        console.error(error);
    };

    const clearTotpChallenge = () => {
        setTotpChallenge(null);
        setChallengeMessage('');
        setAuthError('');
    };

    const readTotpCode = (target: HTMLFormElement) =>
        [0, 1, 2, 3, 4, 5]
            .map((index) => (target.elements.namedItem(`totp_${index}`) as HTMLInputElement | null)?.value ?? '')
            .join('');

    const onSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        setSubmittable(false);
        setAuthError('');
        try {
            const target = ev.currentTarget;
            const usernameInput = target.elements.namedItem('username') as HTMLInputElement | null;
            const passwordInput = target.elements.namedItem('password') as HTMLInputElement | null;

            const response = totpChallenge
                ? await User.signIn({
                      totpChallenge,
                      otpCode: readTotpCode(target),
                  })
                : await User.signIn({
                      name: usernameInput?.value ?? '',
                      password: await hashPassword(passwordInput?.value ?? ''),
                  });

            if (response.type === 'totp-required') {
                setTotpChallenge(response.totpChallenge);
                setChallengeMessage(response.message);
                return;
            }

            localStorage.setItem('user-info', JSON.stringify(response.user));
            localStorage.setItem('token', response.token);
            setCookie('token', response.token);
            await completeAuthentication(response);
        } catch (error: unknown) {
            reportAuthError(I18N.auth.signInError, error);
        } finally {
            setSubmittable(true);
        }
    };

    const onPasskeySignIn = async () => {
        setSubmittable(false);
        setAuthError('');
        try {
            await authenticatePasskey(userName);
            await completeAuthentication();
        } catch (error: unknown) {
            reportAuthError('Passkey sign-in failed', error);
        } finally {
            setSubmittable(true);
        }
    };

    const onMicrosoftSignIn = async () => {
        if (!submittable) {
            return;
        }
        setSubmittable(false);
        setAuthError('');
        try {
            const result = await beginMicrosoftLogin();
            window.location.href = result.authorizationUrl;
        } catch (error) {
            reportAuthError('Failed to start Microsoft sign-in', error);
            setSubmittable(true);
        }
    };

    return (
        <form onSubmit={onSubmit} className={`mx-auto w-full max-w-sm ${className}`.trim()}>
            {showGreeting && (
                <div className="mb-6">
                    <p className="text-theme-text-muted text-xs font-semibold tracking-wide uppercase">
                        {randomGreeting}
                    </p>
                    <h1 className="text-theme-text mt-2 text-3xl font-semibold">{title}</h1>
                    <p className="text-theme-text-muted mt-2 text-sm leading-5">{description}</p>
                </div>
            )}

            {inlineError && authError && (
                <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-600 dark:border-red-800/30 dark:bg-red-900/20 dark:text-red-400">
                    {authError}
                </div>
            )}

            <div className="space-y-4">
                <GoodInput
                    label={I18N.auth.userName}
                    name="username"
                    disabled={Boolean(totpChallenge)}
                    onChange={(v) => {
                        setUserName(typeof v === 'string' ? v : '');
                        if (totpChallenge) clearTotpChallenge();
                    }}
                />
                <GoodInput
                    label={I18N.auth.password}
                    name="password"
                    type="password"
                    disabled={Boolean(totpChallenge)}
                />
                {totpChallenge && (
                    <div className="space-y-3">
                        {challengeMessage && (
                            <p className="text-theme-text-muted bg-theme-button-secondary rounded-lg px-3 py-2 text-sm">
                                {challengeMessage}
                            </p>
                        )}
                        <GoodPinCode label={I18N.auth.totpCode} name="totp" required />
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={clearTotpChallenge}
                                className="text-theme-link text-sm font-semibold"
                            >
                                {I18N.app.change}
                            </button>
                        </div>
                    </div>
                )}

                <Button type="submit" className="w-full" loading={!submittable} disabled={!submittable}>
                    {totpChallenge ? I18N.app.submit : I18N.auth.signIn}
                </Button>

                {showAgreement && (
                    <p className="text-theme-text-muted text-xs leading-5">{I18N.auth.signInAgreement}</p>
                )}

                {showRegisterLink && (
                    <p className="text-theme-text-muted text-sm">
                        {I18N.auth.notHavingAccount}{' '}
                        <Link to="/auth/register" className="text-theme-link font-semibold">
                            {I18N.auth.register}
                        </Link>
                    </p>
                )}

                {showThirdParty && !totpChallenge && (
                    <>
                        <div className="flex items-center gap-3 pt-1">
                            <div className="border-theme-border-divider flex-1 border-t" />
                            <div className="text-theme-text-muted text-xs font-medium">{I18N.auth.signInWith}</div>
                            <div className="border-theme-border-divider flex-1 border-t" />
                        </div>

                        <div className="grid gap-2 sm:grid-cols-2">
                            <button
                                type="button"
                                onClick={onMicrosoftSignIn}
                                disabled={!submittable}
                                className="border-theme-border-divider bg-theme-content-bg text-theme-text hover:bg-theme-hover focus:ring-theme-focus/20 flex min-h-10 w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition focus:ring-4 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <span className="material-symbols-rounded text-base">mail</span>
                                <span>{I18N.auth.signInWithMicrosoft}</span>
                            </button>
                            <button
                                type="button"
                                onClick={onPasskeySignIn}
                                disabled={!submittable}
                                className="border-theme-border-divider bg-theme-content-bg text-theme-text hover:bg-theme-hover focus:ring-theme-focus/20 flex min-h-10 w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition focus:ring-4 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <span className="material-symbols-rounded text-base">passkey</span>
                                <span>{I18N.auth.signInWithPasskey}</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </form>
    );
}
